import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CloseButton, useDashboardBreakpoint } from '@repo/common';
import { ReactComponent as NavigationIcon } from '@repo/common/assets/icons/20px/icon_20px_navigation-right.svg';

const STEP_POPUP_FALLBACK = {
  step01: {
    score: 65,
    blocker1: 'Language Ability',
    blocker2: 'Cultural Readiness',
    priority: 'Improve your Korean (TOPIK) level',
    gain: 5,
  },
  step02: {
    score: 45,
    blocker1: 'Work Experience',
    blocker2: 'Skills & Portfolio',
    priority: 'Build your resume',
    gain: 5,
  },
  step03: {
    score: 30,
    blocker1: 'Visa Readiness',
    blocker2: 'Career Fit',
    priority: 'Check your visa eligibility',
    gain: 5,
  },
  step04: {
    score: 80,
    blocker1: 'Network & Support',
    blocker2: 'Career Fit',
    priority: 'Expand your professional network',
    gain: 5,
  },
  step05: {
    score: 90,
    blocker1: 'Career Fit',
    blocker2: 'Financial Readiness',
    priority: 'Apply to your target companies',
    gain: 5,
  },
};

const CATEGORY_LABELS = {
  c1Score: 'Goals & Direction',
  c2Score: 'Korean Background',
  c3Score: 'Work Experience',
  c4Score: 'Skills & Portfolio',
  c5Score: 'Language Ability',
  o1Score: 'Visa Readiness',
  o2Score: 'Financial Readiness',
  o3Score: 'Academic Fit',
  o4Score: 'Career Fit',
  o5Score: 'Cultural Readiness',
  o6Score: 'Network & Support',
};

// finalScore가 0~5 스케일이면 100점 만점으로 환산한다.
const normalizeScore = (score) => {
  const numeric = Number(score);
  if (!Number.isFinite(numeric)) return null;
  const score100 = numeric <= 5 ? numeric * 20 : numeric;
  return Math.round(Math.max(0, Math.min(100, score100)) * 10) / 10;
};

// 가장 낮은 카테고리 2개 = critical blocker Top 1 / Top 2
const getCriticalBlockers = (evaluationData) => {
  if (!evaluationData) return [];
  return Object.entries(CATEGORY_LABELS)
    .map(([key, label]) => ({ label, value: Number(evaluationData[key]) }))
    .filter((item) => Number.isFinite(item.value))
    .sort((a, b) => a.value - b.value)
    .slice(0, 2)
    .map((item) => item.label);
};

// "improve 5 points" 같은 문자열에서 숫자만 추출
const parseGainPoints = (text) => {
  const matched = String(text ?? '').match(/\d+/);
  return matched ? Number(matched[0]) : null;
};

const SidePanner = ({
  selectedStep,
  currentLabels,
  userStep,
  evaluationData,
  isClosing,
  handleClose,
}) => {
  const navigate = useNavigate();
  const bp = useDashboardBreakpoint();
  const isMobile = ['mobileMini', 'mobile', 'tabletH'].includes(bp);
  const positionClass = isMobile ? 'fixed' : 'absolute';

  if (!selectedStep) return null;

  const fallback =
    STEP_POPUP_FALLBACK[selectedStep] || STEP_POPUP_FALLBACK.step01;
  const labelData = currentLabels.find((l) => l.step === selectedStep);

  // 실제 추천 액션(점수 상승용)
  const priorityActions = Array.isArray(
    evaluationData?.recommendationData?.priorityActions,
  )
    ? evaluationData.recommendationData.priorityActions
    : [];
  const topPriority = priorityActions[0];

  // 실제 점수 (없으면 단계별 폴백)
  const realScore = normalizeScore(evaluationData?.finalScore);
  const score = realScore ?? fallback.score;

  // critical blocker Top 1 / Top 2 (없으면 단계별 폴백)
  const realBlockers = getCriticalBlockers(evaluationData);
  const blocker1 = realBlockers[0] || fallback.blocker1;
  const blocker2 = realBlockers[1] || fallback.blocker2;

  // 다음 우선순위 액션 + 예상 상승 점수
  const priorityTitle =
    topPriority?.title || topPriority?.actionText || fallback.priority;
  const priorityUrl = topPriority?.linkUrl;
  const gainPoints =
    parseGainPoints(topPriority?.scoreLabel ?? topPriority?.pointText) ??
    fallback.gain;

  // ── 도넛 그래프 ─────────────────────────────────────────────
  // green = 현재 점수, orange = 다음 우선순위로 올릴 수 있는 점수, gray = 나머지
  const radius = 46;
  const circumference = 2 * Math.PI * radius;
  const scoreRatio = Math.max(0, Math.min(1, score / 100));
  const gainRatio = Math.max(
    0,
    Math.min(1 - scoreRatio, (gainPoints || 0) / 100),
  );
  const greenLength = circumference * scoreRatio;
  const orangeLength = circumference * gainRatio;

  const handlePriorityClick = () => {
    if (!priorityUrl) return;
    if (/^https?:\/\//.test(priorityUrl)) {
      window.location.assign(priorityUrl);
    } else {
      navigate(priorityUrl);
    }
  };

  return (
    <>
      <div
        onClick={handleClose}
        className={`${positionClass} top-0 bottom-0 right-0 z-50`}
        style={{
          width: '980px',
          maxWidth: '100%',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          maskImage: 'linear-gradient(to right, transparent 45%, black 50%)',
          WebkitMaskImage:
            'linear-gradient(to right, transparent 45%, black 50%)',
          animation: isClosing
            ? 'slideOutRight 0.3s ease-in forwards'
            : 'slideInRight 0.3s ease-out forwards',
        }}
      />

      <div
        className={`${positionClass} top-0 bottom-0 right-0 z-50 flex items-start justify-end p-5`}
        style={{
          animation: isClosing
            ? 'slideOutRight 0.3s ease-in forwards'
            : 'slideInRight 0.3s ease-out forwards',
          pointerEvents: 'none',
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className='flex flex-col gap-[30px] min-w-[460px] max-w-[480px] rounded-[15px] p-5'
          style={{
            pointerEvents: 'auto',
            boxShadow: '-4px 6px 30px 0 rgba(85, 164, 139, 0.20)',
            backdropFilter: 'blur(45px)',
            WebkitBackdropFilter: 'blur(45px)',
            border: '1.6px solid transparent',
            backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8)), linear-gradient(180deg, rgba(255, 209, 129, 0.20) 0%, rgba(126, 234, 201, 0.20) 100%)`,
            backgroundOrigin: 'padding-box, border-box',
            backgroundClip: 'padding-box, border-box',
          }}
        >
          <div className='relative flex flex-col items-center justify-between gap-5'>
            <div className='flex items-start justify-center w-full'>
              <span className='typo-b14Strong text-grayscale-gray04'>
                {labelData?.stepText}
                {'. '}
                <span className='text-green-deep05'>{labelData?.label}</span>
              </span>
              <div className='absolute flex items-start -right-1 -top-1'>
                <CloseButton onClick={handleClose} />
              </div>
            </div>

            <div className='flex items-end w-full gap-4 pl-5'>
              <div className='relative w-[202px] h-[202px] flex-shrink-0'>
                <svg viewBox='0 0 120 120' className='w-full h-full -rotate-90'>
                  {/* 트랙 (전체 원) */}
                  <circle
                    cx='60'
                    cy='60'
                    r={radius}
                    fill='none'
                    stroke='#D0D0D038'
                    strokeWidth='16'
                  />
                  {/* 현재 점수 */}
                  <circle
                    cx='60'
                    cy='60'
                    r={radius}
                    fill='none'
                    stroke='#4DD5A3'
                    strokeWidth='16'
                    strokeDasharray={`${greenLength} ${circumference - greenLength}`}
                    strokeDashoffset={0}
                    strokeLinecap='butt'
                  />
                  {/* 다음 우선순위로 올릴 수 있는 점수 */}
                  {orangeLength > 0 && (
                    <circle
                      cx='60'
                      cy='60'
                      r={radius}
                      fill='none'
                      stroke='#FFD181'
                      strokeWidth='16'
                      strokeDasharray={`${orangeLength} ${circumference - orangeLength}`}
                      strokeDashoffset={-greenLength}
                      strokeLinecap='butt'
                    />
                  )}
                </svg>
                <div className='absolute inset-0 flex flex-col items-center justify-center'>
                  <span className='font-bold typo-h3 text-green-deep05'>
                    {score}
                  </span>
                  <span className='typo-b13Strong text-text-placeholder'>
                    Readiness Score
                  </span>
                </div>
              </div>

              <div className='flex flex-col w-full gap-1 py-2'>
                <div className='flex items-center gap-1.5'>
                  <span className='flex-shrink-0 w-3 h-3 border rounded-sm border-green-deep02 bg-green-deep01' />
                  <span className='typo-b14 text-text-secondary'>
                    {blocker1}
                  </span>
                </div>
                <div className='flex items-center gap-1.5'>
                  <span className='flex-shrink-0 w-3 h-3 border rounded-sm border-orange-orange03 bg-orange-orange02' />
                  <span className='typo-b14 text-text-secondary'>
                    {blocker2}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <button
            type='button'
            onClick={handlePriorityClick}
            disabled={!priorityUrl}
            className={`flex items-center justify-between w-full gap-3 px-5 py-[15px] rounded-[10px] min-h-[60px] bg-grayscale-gray00 text-green-deep05 shadow-3 ${
              priorityUrl
                ? 'cursor-pointer transition-colors hover:bg-green-light01'
                : 'cursor-default'
            }`}
          >
            <span className='flex-1 text-left typo-b18Strong'>
              Your next priority is to {priorityTitle}
            </span>
            <NavigationIcon className='flex-shrink-0 -rotate-45 opacity-60' />
          </button>
        </div>
      </div>
    </>
  );
};

export default SidePanner;
